import React, { useEffect, useState, useCallback } from 'react';

import axios from 'axios';
import { CategoryColorMap, Post } from '../types';
import { API_URL } from '../config';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './Home.css';

const defaultCategoryColors: CategoryColorMap = {
  'fire-alarm': { background: '#ffeeee', text: '#cc3333' },
  sprinkler: { background: '#ddeeff', text: '#3366cc' },
  pavement: { background: '#ffeedd', text: '#996633' },
  maintenance: { background: '#ddffee', text: '#33cc66' },
  announcement: { background: '#eeeeff', text: '#6633cc' },
  other: { background: '#eeeeee', text: '#666666' },
};

const Home: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [categoryColors, setCategoryColors] = useState<CategoryColorMap>(defaultCategoryColors);

  const fetchPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/posts`);
      setPosts(response.data.posts);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);
  
  useEffect(() => {
    const fetchColors = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/posts/category-colors`);
        setCategoryColors({
          ...defaultCategoryColors,
          ...(response.data.colors || {}),
        });
      } catch (error) {
        console.error('Failed to load category colors:', error);
        setCategoryColors(defaultCategoryColors);
      }
    };

    fetchColors();
  }, []);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="home-container">
      <div className="home-header">
        <h1>Building Updates</h1>
        <p>Stay informed about maintenance, updates, and announcements</p>
      </div>

      <div className="airpark-disclaimer">
        <p><strong>For any questions regarding the Airpark, YOU NEED TO CONTACT A TRUSTEE.</strong> Please note, anyone that reaches out to any Airpark vendor will be responsible for any costs incurred with that inquiry.</p>
      </div>

      <div className="posts-grid">
        {posts.length === 0 ? (
          <div className="no-posts">
            <p>No updates available yet.</p>
          </div>
        ) : (
          posts.map((post) => {
            const colors = categoryColors[post.category];
            return (
              <div key={post._id} className="post-card">
                <div className="post-header">
                  <span
                    className={`post-category ${post.category}`}
                    style={
                      colors
                        ? {
                            backgroundColor: colors.background,
                            color: colors.text,
                          }
                        : undefined
                    }
                  >
                    {post.category.replace('-', ' ')}
                  </span>
                  <span className="post-date">{formatDate(post.lastUpdated)}</span>
                </div>
                <div className="post-title-row">
                  <h3 className="post-title">{post.title}</h3>
                  {(user?.role === 'admin' || user?.role === 'trustee') && (
                    <button 
                      className="quick-edit-btn"
                      onClick={() => navigate('/admin')}
                      title="Edit in Admin Panel"
                    >
                      Edit
                    </button>
                  )}
                </div>
                <p className="post-description">{post.description?.replace(/^[•\-*]\s*/gm, '')}</p>
                {(() => {
                  const attachments =
                    post.attachments && post.attachments.length > 0
                      ? post.attachments
                      : post.attachment
                      ? [post.attachment]
                      : [];
                  if (attachments.length === 0) return null;
                  return (
                    <div className="post-attachment">
                      {attachments.map((a) => (
                        <div key={a.filename}>
                          <a
                            href={`${API_URL}${a.url}`}
                            download={a.originalName}
                            className="attachment-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Download: {a.originalName}
                          </a>
                        </div>
                      ))}
                    </div>
                  );
                })()}
                <div className="post-footer">
                  <div className="responsible-party">
                    <strong>Service Provider:</strong> {post.responsibleParty}
                  </div>
                  {post.contactInfo && (
                    <div className="contact-info">
                      <strong>Contact:</strong> {post.contactInfo}
                    </div>
                  )}
                  {post.nextScheduledDate && (
                    <div className="next-date">
                      <strong>Next Scheduled:</strong> {formatDate(post.nextScheduledDate)}
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Home;

